import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  useTemplates, 
  useDeleteTemplate,
  TokenStandard, 
  AuditStatus 
} from '../../../hooks/useSmartContractTemplates';
import { AuditStatusBadge } from '../../../components/smart-contracts';
import { PageHeader } from '../../../components/help/PageHeader';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { 
  Plus, 
  Search, 
  Filter, 
  Eye, 
  Edit, 
  Trash2, 
  FileCode,
  MoreVertical,
  Shield
} from 'lucide-react';
import { useAuthStore } from '../../../stores/auth.store';

const tokenStandardLabels: Record<TokenStandard, string> = {
  ERC20: 'ERC-20',
  ERC1400: 'ERC-1400',
  ERC4626: 'ERC-4626',
};

export function SmartContractTemplateList() {
  const navigate = useNavigate();
  const { user, isSuperAdmin } = useAuthStore();
  const isSuperAdminUser = isSuperAdmin();
  
  const [search, setSearch] = useState('');
  const [selectedStandard, setSelectedStandard] = useState<TokenStandard | undefined>();
  const [selectedAuditStatus, setSelectedAuditStatus] = useState<AuditStatus | undefined>();
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useTemplates({
    tokenStandard: selectedStandard,
    auditStatus: selectedAuditStatus,
    search: search || undefined,
    page,
    limit: 10,
  });

  const deleteMutation = useDeleteTemplate();

  const handleDelete = async (id: string, name: string) => {
    if (confirm(`Tem certeza que deseja excluir o template "${name}"?`)) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const templates = data?.data || [];
  const totalPages = data?.totalPages || 1;

  return (
    <div className="p-6 space-y-6">
      <PageHeader
        title="Smart Contracts"
        description="Gerencie os templates de smart contracts para tokenização de ativos"
        helpContent={`
## Templates de Smart Contracts

Templates de smart contracts são modelos de código Solidity que podem ser usados para criar tokens na blockchain.

### Como Usar

1. Visualize os templates disponíveis
2. Crie novos templates (Super Admin)
3. Registre auditorias de segurança
4. Use templates auditados para deploy em produção

### Dicas

- Templates não auditados só podem ser usados em redes de teste
- Sempre verifique o status de auditoria antes de usar em produção
- O código é versionado automaticamente a cada alteração
        `}
      />

      {/* Filters */}
      <div className="bg-surface-800 rounded-lg p-4 border border-surface-700">
        <div className="flex flex-wrap gap-4 items-end">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Token Standard Filter */}
          <select
            value={selectedStandard || ''}
            onChange={(e) => setSelectedStandard(e.target.value as TokenStandard || undefined)}
            className="w-44 px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os padrões</option>
            <option value="ERC20">ERC-20</option>
            <option value="ERC1400">ERC-1400</option>
            <option value="ERC4626">ERC-4626</option>
          </select>

          {/* Audit Status Filter */}
          <select
            value={selectedAuditStatus || ''}
            onChange={(e) => setSelectedAuditStatus(e.target.value as AuditStatus || undefined)}
            className="w-44 px-3 py-2 bg-surface-900 border border-surface-700 rounded-lg text-surface-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="">Todos os status</option>
            <option value="PENDING_AUDIT">Pendente</option>
            <option value="AUDITED">Auditado</option>
            <option value="AUDIT_FAILED">Falhou</option>
          </select>

          {/* Create Button (Super Admin only) */}
          {isSuperAdminUser && (
            <Button onClick={() => navigate('/admin/tokenization/contracts/new')}>
              <Plus className="w-4 h-4 mr-2" />
              Novo Template
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-surface-800 rounded-lg border border-surface-700 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12 text-red-400">
            Erro ao carregar templates
          </div>
        ) : templates.length === 0 ? (
          <div className="text-center py-12">
            <FileCode className="w-12 h-12 text-surface-500 mx-auto mb-4" />
            <p className="text-surface-400">Nenhum template encontrado</p>
            {isSuperAdminUser && (
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => navigate('/admin/tokenization/contracts/new')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Criar primeiro template
              </Button>
            )}
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-900">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-300">Nome</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-300">Padrão</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-300">Versão</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-300">Auditoria</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-300">Deploys</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-surface-300">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {templates.map((template) => (
                <tr key={template.id} className="hover:bg-surface-700/50 transition-colors">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-surface-100">{template.name}</p>
                      {template.description && (
                        <p className="text-sm text-surface-400 truncate max-w-xs">
                          {template.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Badge variant="info">
                      {tokenStandardLabels[template.tokenStandard]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-surface-300">v{template.currentVersion}</span>
                  </td>
                  <td className="px-4 py-3">
                    <AuditStatusBadge
                      status={template.auditStatus}
                      auditorName={template.auditorCompany}
                      auditDate={template.auditDate}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-surface-300">
                      {template._count?.deployments || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-2"
                        onClick={() => navigate(`/admin/tokenization/contracts/${template.id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      {isSuperAdminUser && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2"
                            onClick={() => navigate(`/admin/tokenization/contracts/${template.id}/audit`)}
                          >
                            <Shield className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2 text-red-400 hover:text-red-300"
                            onClick={() => handleDelete(template.id, template.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-surface-700">
            <p className="text-sm text-surface-400">
              Página {page} de {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(p => p - 1)}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page === totalPages}
                onClick={() => setPage(p => p + 1)}
              >
                Próxima
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SmartContractTemplateList;
